<template>
  <v-container class="px-10 news-page" fluid>
    <v-row>
      <v-col class="py-0">
        <v-breadcrumbs
          :items="breadcrumbs"
          divider=">"
          class="px-0 py-2"
        ></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div class="head-left">
        <h3 class="pageHeadline mb-1" t-data="page-headline">
          News & Articles <span class="thai-subtitle">ข่าวสารและบทความ</span>
        </h3>
        <p class="page-description mb-0">
          Publish health articles, hospital news, promotions and press releases to branch sites and the central portal.
        </p>
      </div>

      <v-btn
        color="primary"
        class="add-btn"
        depressed
        @click="goToCreateArticle"
      >
        <v-icon left small>mdi-plus</v-icon>
        Create New Article
      </v-btn>
    </div>

    <div class="pill-tabs-bar mb-4">
      <button
        type="button"
        :class="['tab-pill', { active: isArticleListTab }]"
        @click="goToArticlesTab"
      >
        Articles List
      </button>
      <button
        type="button"
        :class="['tab-pill', { active: isCategoriesTab }]"
        @click="goToCategoriesTab"
      >
        Categories & Tags Setup
      </button>
    </div>

    <v-card class="table-card" elevation="0">
      
      <div class="card-head pa-4 d-flex align-center justify-space-between">
        <div>
          <h4 class="category-card-title mb-1">Category configuration</h4>
          <p class="category-card-sub mb-0">Central categories are shared by every branch site.</p>
        </div>
        <v-btn
          color="primary"
          depressed
          class="add-btn"
          @click="openAddCategoryModal"
        >
          <v-icon left small>mdi-plus</v-icon>
          Add New Category
        </v-btn>
      </div>

      <v-data-table
        :headers="table.headers"
        :items="paginatedCategoryList"
        :loading="isCategoryLoading"
        :items-per-page="table.pageSize"
        hide-default-footer
        class="category-table"
      >
        <template v-slot:item.id="{ item }">
          <span class="id-text">{{ item.id }}</span>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="py-2">
            <div class="cat-title-th">{{ item.name_th }}</div>
            <div class="cat-title-en">{{ item.name_en || '-' }}</div>
          </div>
        </template>

        <template v-slot:item.scope="{ item }">
          <span :class="['scope-pill', (item.org_codename === 'central' || item.scope === 'Central') ? 'central' : 'branch']">
            {{ (item.org_codename === 'central' || item.scope === 'Central') ? 'Central' : item.scope }}
          </span>
        </template>

        <template v-slot:item.total_articles="{ item }">
          <span class="count-text">{{ item.total_articles }}</span>
        </template>

        <template v-slot:item.order="{ item }">
          <span class="order-text">{{ item.order }}</span>
        </template>

        <template v-slot:item.status="{ item }">
          <span :class="['status-pill', item.isActive ? 'status-green' : 'status-gray']">
            <span class="dot"></span>
            {{ item.isActive ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center gap-1">
            <button class="action-icon-btn" title="Edit" @click="openEditCategoryModal(item)">
              <v-icon size="15">mdi-pencil-outline</v-icon>
            </button>
            <button class="action-icon-btn" title="Delete" @click="promptDeleteCategory(item)">
              <v-icon size="15">mdi-delete-outline</v-icon>
            </button>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="py-8 text-center grey--text">
            <v-icon size="40" color="grey lighten-1">mdi-tag-outline</v-icon>
            <div class="mt-2 text-caption">No categories found</div>
          </div>
        </template>
      </v-data-table>

       <div class="table-foot">
        <span class="footer-entries">
          Showing {{ pageStart }} to {{ pageEnd }} of {{ totalDisplayCount }} entries
        </span>
        <v-pagination
          v-model="paginationPage"
          :length="pageLength"
          :total-visible="7"
          color="primary"
          @input="onPageChange"
        ></v-pagination>
      </div>

    </v-card>

    <v-dialog
      v-model="isCategoryModalOpen"
      max-width="520px"
      persistent
      content-class="category-modal-dialog"
    >
      <div class="category-modal-content">
        <div class="modal-header d-flex align-center justify-space-between pa-5">
          <div>
            <h4 class="modal-title mb-1">
              {{ isEditMode ? 'Edit Category' : 'Add New Category' }}
            </h4>
            <p class="modal-subtitle mb-0">
              Configure category details, branch visibility, and display order.
            </p>
          </div>
          <button type="button" class="close-x-btn" @click="closeCategoryModal">
            <v-icon size="18" color="#64748b">mdi-close</v-icon>
          </button>
        </div>

        <div class="modal-body pa-5">
          
          <div class="lang-switch-bar mb-4">
            <button
              type="button"
              :class="['lang-btn', { active: currentLang === 'th' }]"
              @click="currentLang = 'th'"
            >
            TH · ไทย           
           </button>
            <button
              type="button"
              :class="['lang-btn', { active: currentLang === 'en' }]"
              @click="currentLang = 'en'"
            >
            EN · English
            </button>
          </div>

          <div v-show="currentLang === 'th'" class="form-group mb-4">
            <label class="form-label">
              CATEGORY NAME (TH) <span class="required-star">*</span>
            </label>
            <input
              type="text"
              v-model="categoryForm.name_th"
              class="form-input"
              :class="{ 'has-error': formErrors.name_th }"
              placeholder="เช่น บทความสุขภาพ, ข่าวสารโรงพยาบาล..."
              @input="formErrors.name_th = ''"
            />
            <div v-if="formErrors.name_th" class="error-text mt-1">
              {{ formErrors.name_th }}
            </div>
          </div>

          <div v-show="currentLang === 'en'" class="form-group mb-4">
            <label class="form-label">
              CATEGORY NAME (EN) <span class="optional-tag">(Optional)</span>
            </label>
            <input
              type="text"
              v-model="categoryForm.name_en"
              class="form-input"
              placeholder="e.g. Medical Knowledge, Hospital News..."
            />
          </div>

          <div class="form-group mb-4">
            <label class="form-label">
              BRANCH SCOPE <span class="required-star">*</span>
            </label>
            <div class="select-field-wrap">
              <select v-model="categoryForm.org_codename" class="custom-select-box">
                <option v-for="branch in branchOptions" :key="branch.codename" :value="branch.codename">
                  {{ branch.name }}
                </option>
              </select>
              <v-icon size="18" class="select-chevron" color="#64748b">mdi-chevron-down</v-icon>
            </div>
            <div class="field-hint mt-1">
              Select "PRINCGroup Central" to share this category across all hospital branches.
            </div>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">DISPLAY ORDER</label>
            <input
              type="number"
              v-model.number="categoryForm.order"
              class="form-input"
              min="1"
              placeholder="1"
            />
            <div class="field-hint mt-1">
              Lower numbers appear first in the category dropdowns and filter lists.
            </div>
          </div>

          <div class="form-group mb-2">
            <div class="status-toggle-box d-flex align-center justify-space-between pa-3">
              <div>
                <div class="status-toggle-title">Category Status</div>
                <div class="status-toggle-desc">
                  {{ categoryForm.status === 0 ? 'Active (Visible across portal & articles)' : 'Inactive (Hidden from users)' }}
                </div>
              </div>
              <v-switch
                v-model="categoryForm.status"
                :true-value="0"
                :false-value="1"
                color="#2563eb"
                hide-details
                inset
                class="ma-0 pa-0"
              ></v-switch>
            </div>
          </div>

        </div>

        <div class="modal-footer pa-4 d-flex align-center justify-end gap-2">
          <button type="button" class="btn-cancel" @click="closeCategoryModal">
            Cancel
          </button>
          <button
            type="button"
            class="btn-save"
            :disabled="isSaving"
            @click="saveCategory"
          >
            <v-progress-circular
              v-if="isSaving"
              indeterminate
              size="15"
              width="2"
              color="#ffffff"
              class="mr-2"
            ></v-progress-circular>
            <span>{{ isEditMode ? 'Update Category' : 'Save Category' }}</span>
          </button>
        </div>

      </div>
    </v-dialog>

    <v-dialog-confirm
      v-if="itemToDelete"
      :show="isDeleteDialogOpen"
      :subTitle="deleteConfirmSubTitle"
      title="Delete Confirmation"
      headlineColor="red"
      confirmBtnColor="red"
      buttonText="Delete"
      :close="closeDeleteDialog"
      :confirm="confirmDeleteCategory"
    />

    <snack-bar v-model="isSuccessSnackbar" :content="snackbarMessage"></snack-bar>
    <snack-bar v-model="isFailSnackbar" :content="snackbarErrorMessage" type="error"></snack-bar>

  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AlertConfirm from '@/components/AlertConfirm'
import SnackBar from '@/components/SnackBar'

export default {
  name: 'NewsCategories',
  components: {
    'v-dialog-confirm': AlertConfirm,
    SnackBar
  },
  data() {
    return {
      isSaving: false,
      isEditMode: false,
      isCategoryModalOpen: false,
      isDeleteDialogOpen: false,
      itemToDelete: null,
      currentLang: 'th',
      isSuccessSnackbar: false,
      isFailSnackbar: false,
      snackbarMessage: '',
      snackbarErrorMessage: '',
      breadcrumbs: [
        { text: 'Dashboard', exact: true, href: '/' },
        { text: 'News & Articles', disabled: true, href: '/news-list' },
      ],
      paginationPage: 1,
      table: {
        pageSize: 10,
        headers: [
          { text: 'ID', value: 'id', width: '70px', sortable: false },
          { text: 'CATEGORY NAME (TH / EN)', value: 'name', sortable: false },
          { text: 'SCOPE', value: 'scope', sortable: false },
          { text: 'TOTAL ARTICLES', value: 'total_articles', sortable: false },
          { text: 'DISPLAY ORDER', value: 'order', sortable: false },
          { text: 'STATUS', value: 'status', sortable: false },
          { text: 'ACTIONS', value: 'actions', sortable: false, align: 'center', width: '100px' }
        ]
      },
      categoryForm: {
        id: null,
        name_th: '',
        name_en: '',
        org_codename: 'central',
        order: 1,
        status: 0
      },
      formErrors: {
        name_th: ''
      }
    }
  },
  computed: {
    ...mapState('news', [
      'categoryTableList',
      'categoryTotal',
      'categoryCurrentPage',
      'isCategoryLoading',
      'orgList'
    ]),
    isArticleListTab() {
      return this.$route.path === '/news-list' || this.$route.name === 'news-list'
    },
    isCategoriesTab() {
      return this.$route.path === '/news-categories' || this.$route.name === 'news-categories'
    },
    branchOptions() {
      const allOrgs = (this.orgList && this.orgList.all) || []
      return [
        { name: 'PRINCGroup Central', codename: 'central' },
        ...allOrgs
          .filter(o => (o.codename || '').toLowerCase() !== 'central')
          .map(o => ({
            name: o.name || (o.codename || '').toUpperCase(),
            codename: o.codename
          }))
      ]
    },
    formattedCategoryList() {
      return (this.categoryTableList || []).map((cat, index) => {
        const contents = cat.category_contents || cat.contents || []
        const th = contents.find(c => c.code === 'th') || contents[0] || {}
        const en = contents.find(c => c.code === 'en') || {}
        const orgs = cat.org || cat.orgs || []

        let orgCodename = cat.org_codename
        if (!orgCodename) {
          if (orgs.length === 1 && orgs[0].org_codename && orgs[0].org_codename.toLowerCase() !== 'central') {
            orgCodename = orgs[0].org_codename
          } else {
            orgCodename = 'central'
          }
        }

        const isCentral = orgCodename.toLowerCase() === 'central'
        const isActive = Number(cat.status) === 0 || cat.status === '0' || cat.status === 'active'

        return {
          id: cat.id,
          name_th: th.title || cat.title_th || cat.name_th || cat.name || `Category #${cat.id}`,
          name_en: en.title || cat.title_en || cat.name_en || '',
          org_codename: isCentral ? 'central' : orgCodename,
          scope: isCentral ? 'Central' : orgCodename.toUpperCase(),
          total_articles: cat.total_articles !== undefined ? cat.total_articles : 0,
          order: cat.order || (index + 1),
          status: cat.status !== undefined ? Number(cat.status) : 0,
          isActive,
        }
      })
    },

    totalDisplayCount() {
      return (this.formattedCategoryList && this.formattedCategoryList.length) || 0
    },

    paginatedCategoryList() {
      const size = (this.table && this.table.pageSize) || 10
      const start = (this.paginationPage - 1) * size
      return this.formattedCategoryList.slice(start, start + size)
    },

    pageLength() {
      const size = (this.table && this.table.pageSize) || 10
      const total = this.totalDisplayCount
      return Math.max(1, Math.ceil(total / size))
    },

    pageStart() {
      const total = this.totalDisplayCount
      if (!total) return 0
      const size = (this.table && this.table.pageSize) || 10
      return (this.paginationPage - 1) * size + 1
    },

    pageEnd() {
      const total = this.totalDisplayCount
      if (!total) return 0
      const size = (this.table && this.table.pageSize) || 10
      return Math.min(this.paginationPage * size, total)
    },


    deleteConfirmSubTitle() {
      if (!this.itemToDelete) return ''
      if (this.itemToDelete.total_articles > 0) {
        return `Warning: Category "${this.itemToDelete.name_th}" is currently linked to ${this.itemToDelete.total_articles} article(s). Please reassign them first.`
      }
      return `Are you sure you want to delete category "${this.itemToDelete.name_th}"?`
    }
  },
  async mounted() {
    if (this.setListOrgInfo) {
      this.setListOrgInfo()
    }
    this.paginationPage = this.categoryCurrentPage || 1
    await this.fetchCategoriesTable()
  },
  methods: {
    ...mapActions('news', [
      'setListOrgInfo',
      'fetchCategoriesTable',
      'createCategory',
      'updateCategory',
      'deleteCategory',
      'setCategoryPage'
    ]),

    onPageChange(page) {
      this.paginationPage = page
      this.setCategoryPage(page)
    },

    goToArticlesTab() {
      if (this.$route.path !== '/news-list') {
        this.$router.push('/news-list').catch(() => {})
      }
    },
    goToCategoriesTab() {
      if (this.$route.path !== '/news-categories') {
        this.$router.push('/news-categories').catch(() => {})
      }
    },
    goToCreateArticle() {
      this.$router.push({ name: 'news-create' }).catch(() => {})
    },

    openAddCategoryModal() {
      this.isEditMode = false
      this.currentLang = 'th'
      this.formErrors.name_th = ''
      this.categoryForm = {
        id: null,
        name_th: '',
        name_en: '',
        org_codename: 'central',
        order: (this.categoryTotal || this.formattedCategoryList.length) + 1,
        status: 0
      }
      this.isCategoryModalOpen = true
    },

    openEditCategoryModal(item) {
      this.isEditMode = true
      this.currentLang = 'th'
      this.formErrors.name_th = ''
      this.categoryForm = {
        id: item.id,
        name_th: item.name_th || '',
        name_en: item.name_en || '',
        org_codename: item.org_codename || 'central',
        order: item.order || 1,
        status: item.isActive ? 0 : 1
      }
      this.isCategoryModalOpen = true
    },

    closeCategoryModal() {
      this.isCategoryModalOpen = false
      this.formErrors.name_th = ''
    },

    async saveCategory() {
      if (!this.categoryForm.name_th || !this.categoryForm.name_th.trim()) {
        this.currentLang = 'th'
        this.formErrors.name_th = 'Category Name (TH) is required'
        return
      }

      this.isSaving = true
      const payload = {
        title_th: this.categoryForm.name_th.trim(),
        title_en: (this.categoryForm.name_en && this.categoryForm.name_en.trim()) || '',
        org_codename: this.categoryForm.org_codename,
        order: Number(this.categoryForm.order) || 1,
        status: Number(this.categoryForm.status) === 0 ? 0 : 1
      }

      try {
        if (this.isEditMode && this.categoryForm.id) {
          await this.updateCategory({
            id: this.categoryForm.id,
            data: payload
          })
          this.snackbarMessage = 'Category updated successfully'
        } else {
          await this.createCategory(payload)
          this.snackbarMessage = 'Category created successfully'
        }
        this.isSuccessSnackbar = true
        this.closeCategoryModal()
      } catch (err) {
        this.snackbarErrorMessage = (err.response && err.response.data && err.response.data.message) || 'Failed to save category'
        this.isFailSnackbar = true
      } finally {
        this.isSaving = false
      }
    },

    promptDeleteCategory(item) {
      this.itemToDelete = item
      this.isDeleteDialogOpen = true
    },

    closeDeleteDialog() {
      this.isDeleteDialogOpen = false
      this.itemToDelete = null
    },

    async confirmDeleteCategory() {
      if (!this.itemToDelete) return
      try {
        await this.deleteCategory(this.itemToDelete.id)
        this.snackbarMessage = 'Category deleted successfully'
        this.isSuccessSnackbar = true
      } catch (err) {
        this.snackbarErrorMessage = (err.response && err.response.data && err.response.data.message) || 'Failed to delete category'        
        this.isFailSnackbar = true
      } finally {
        this.closeDeleteDialog()
      }
    }
  }
}
</script>

<style scoped>
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.pageHeadline { font-size: 22px; font-weight: 700; color: #0f172a; }
.thai-subtitle { font-weight: 400; font-size: 15px; color: #64748b; margin-left: 8px; }
.page-description { font-size: 13px; color: #64748b; }

.add-btn { text-transform: none; font-weight: 600; border-radius: 8px; }

.pill-tabs-bar { display: flex; align-items: center; gap: 10px; }
.tab-pill {
  padding: 8px 20px;
  border-radius: 999px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  color: #475569;
  outline: none;
  transition: all 0.2s ease;
}
.tab-pill:hover { background-color: #f8fafc; color: #0f172a; }
.tab-pill.active {
  background-color: #eef4ff;
  border-color: #c7d9fc;
  color: #1e40af;
  font-weight: 600;
}

.table-card {
  border: 1px solid #e6e9f0 !important;
  border-radius: 10px !important;
  background: #ffffff !important;
}
.card-head { border-bottom: 1px solid #eef2f7; }
.category-card-title { font-size: 16px; font-weight: 700; color: #0f172a; }
.category-card-sub { font-size: 13px; color: #64748b; }

.category-table ::v-deep th {
  font-size: 11.5px !important;
  font-weight: 700 !important;
  color: #64748b !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
}

.id-text { color: #64748b; font-weight: 500; font-size: 13px; }
.cat-title-th { font-size: 13.5px; font-weight: 700; color: #0f172a; }
.cat-title-en { font-size: 12px; color: #64748b; }

.scope-pill {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
}
.scope-pill.central { background: #f3e8ff; color: #7c3aed; }
.scope-pill.branch { background: #eef1f6; color: #5b6472; }

.count-text, .order-text { font-size: 13px; font-weight: 600; color: #0f172a; }

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 12px;
}
.status-pill .dot { width: 6px; height: 6px; border-radius: 50%; }
.status-pill.status-green { background: #f0fdf4; color: #15803d; }
.status-pill.status-green .dot { background: #1aa964; }
.status-pill.status-gray { background: #f8fafc; color: #64748b; }
.status-pill.status-gray .dot { background: #94a3b8; }

.action-icon-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-icon-btn:hover { background: #f8fafc; color: #0f172a; }

.table-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  font-size: 12px;
  color: #64748b;
  border-top: 1px solid #eef2f7;
}

.footer-entries {
  font-size: 12.5px;
  color: #64748b;
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }

::v-deep .category-modal-dialog {
  border-radius: 14px !important;
  background: #ffffff !important;
  overflow: hidden !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.category-modal-content {
  background: #ffffff;
  border-radius: 14px;
}

.modal-header {
  border-bottom: 1px solid #f1f5f9;
}
.modal-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}
.modal-subtitle {
  font-size: 12.5px;
  color: #64748b;
}

.close-x-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f8fafc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}
.close-x-btn:hover { background: #f1f5f9; }

.lang-switch-bar {
  display: inline-flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
  width: 100%;
}
.lang-btn {
  flex: 1;
  padding: 7px 12px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.lang-btn.active {
  background: #ffffff;
  color: #2563eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.form-group {
  display: flex;
  flex-direction: column;
}
.form-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.required-star { color: #ef4444; font-weight: 700; }
.optional-tag { color: #94a3b8; font-weight: 400; text-transform: none; }

.form-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13.5px;
  color: #0f172a;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: #ffffff;
}
.form-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}
.form-input.has-error {
  border-color: #ef4444 !important;
}
.error-text {
  font-size: 11.5px;
  color: #ef4444;
  font-weight: 500;
}

.select-field-wrap {
  position: relative;
  width: 100%;
}
.custom-select-box {
  width: 100%;
  padding: 9px 36px 9px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  color: #0f172a;
  background-color: #ffffff;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}
.custom-select-box:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}
.select-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.field-hint {
  font-size: 11.5px;
  color: #94a3b8;
}

.status-toggle-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.status-toggle-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}
.status-toggle-desc {
  font-size: 12px;
  color: #64748b;
}

.modal-footer {
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}
.btn-cancel {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}
.btn-cancel:hover { background: #f8fafc; }

.btn-save {
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  background: #2563eb;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.btn-save:hover { background: #1d4ed8; }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
